import { createContext, createElement, type ReactNode, useContext } from "react";

/** Something another module can link to: a file, a folder, a record… */
export interface LinkTarget {
  /** Stable id within its source. */
  id: string;
  /** What the link shows, e.g. the file name. */
  title: string;
  /** Same-origin address that opens it, e.g. "/drive/files/<id>". */
  url: string;
  /** Short kind for the picker, e.g. "Folder", "PDF". */
  kind?: string;
  /** Where it is, e.g. "Legal / Payments". */
  hint?: string;
  /** It holds other targets (a folder): pickers let people open it with `browse`. */
  container?: boolean;
}

/**
 * A module's offer of things to link to (docs/adr/0021). Other modules never import or call that
 * module: the shell collects the sources of the modules the user may see and hands them over through
 * `useLinkSources`, and a picked target is just a URL.
 */
export interface LinkSource {
  /** Unique, e.g. "drive". */
  id: string;
  /** Shown in the picker, e.g. "Documents Drive". */
  label: string;
  icon?: string;
  /** Targets whose name matches the query; an empty query lists a starting set (e.g. recent or top level). */
  search: (query: string, signal: AbortSignal) => Promise<LinkTarget[]>;
  /**
   * What is inside the container reached by following these titles from the top (e.g.
   * ["Legal", "Payments"]); [] is the top level. Rejects when the path doesn't exist.
   */
  browse?: (path: string[], signal: AbortSignal) => Promise<LinkTarget[]>;
}

const LinkSourcesContext = createContext<LinkSource[]>([]);

/** Set by the shell with the link sources of the visible modules. */
export function LinkSourcesProvider({ sources, children }: { sources: LinkSource[]; children: ReactNode }): React.JSX.Element {
  return createElement(LinkSourcesContext.Provider, { value: sources }, children);
}

/** The link sources of the modules the user may see (none outside the shell). */
export function useLinkSources(): LinkSource[] {
  return useContext(LinkSourcesContext);
}
